/**
 * Continuity Status — pure client-side helper.
 *
 * Given one investment record, decide the soft status label the Vault
 * screen should show alongside it. Three levels only, no numbers, no
 * urgency:
 *   - "Complete"     — the four core continuity questions are all answered
 *   - "In Progress"  — some are answered but not all
 *   - "At Risk"      — none of them are answered yet
 *
 * "The four core continuity questions" are:
 *   1. What is it?              → name AND type (always present, so this is free)
 *   2. Where is it held?        → institution or continuity_holder_contact_name
 *   3. How much?                → amount OR current_value (always present, so this is free)
 *   4. Where's the paperwork?   → continuity_paperwork_location AND continuity_access_instructions
 *
 * Because Q1 and Q3 are always answered when an investment exists, the
 * label really tracks Q2 and Q4.
 */

const LEVEL = Object.freeze({
  AT_RISK: 'at_risk',
  IN_PROGRESS: 'in_progress',
  COMPLETE: 'complete',
});

const LABEL = {
  [LEVEL.AT_RISK]: 'Needs details',
  [LEVEL.IN_PROGRESS]: 'In progress',
  [LEVEL.COMPLETE]: 'Complete',
};

// All three states now sit in the calm emerald/amber family — no red. A newly-
// enrolled Continuity user shouldn't be greeted with a wall of "At Risk"
// alarms just because they haven't had time to fill in details yet. Amber
// says "there's something for you to do", green says "you've done it".
const CLASSES = {
  [LEVEL.AT_RISK]: 'bg-amber-50 text-amber-800 border-amber-200',
  [LEVEL.IN_PROGRESS]: 'bg-amber-50 text-amber-800 border-amber-200',
  [LEVEL.COMPLETE]: 'bg-emerald-50 text-emerald-800 border-emerald-200',
};

const hasText = (v) => !!(v && String(v).trim());

const holderFromInvestment = (inv) => {
  const first = (inv?.continuity_holder_first_name || '').trim();
  const last = (inv?.continuity_holder_last_name || '').trim();
  const role = (inv?.continuity_holder_role || '').trim();
  const composed = `${first} ${last}`.trim();
  const name = composed || (inv?.continuity_holder_contact_name || '').trim();
  return { name, role };
};

export function computeContinuityStatus(inv) {
  if (!inv) return { level: LEVEL.AT_RISK, label: LABEL[LEVEL.AT_RISK], className: CLASSES[LEVEL.AT_RISK] };

  const { name } = holderFromInvestment(inv);
  const hasHolder = hasText(inv.institution) || hasText(name);
  const hasPaperwork = hasText(inv.continuity_paperwork_location) && hasText(inv.continuity_access_instructions);

  const answered = [hasHolder, hasPaperwork].filter(Boolean).length;

  let level;
  if (answered === 2) level = LEVEL.COMPLETE;
  else if (answered === 1) level = LEVEL.IN_PROGRESS;
  else level = LEVEL.AT_RISK;

  return { level, label: LABEL[level], className: CLASSES[level] };
}

export { holderFromInvestment };
export const CONTINUITY_LEVEL = LEVEL;
